<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div v-if="showHeader" class="flex items-center justify-between border-b border-white/10 px-5 py-4">
      <div><h2 class="text-sm font-bold">Moderator access</h2><p class="mt-0.5 text-xs text-zinc-500">Share a private control-room link</p></div>
      <span class="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider" :class="access ? 'bg-emerald-400/10 text-emerald-300' : 'bg-zinc-400/10 text-zinc-500'">{{ access ? 'Active' : 'Not created' }}</span>
    </div>

    <div class="space-y-4 overflow-y-auto p-5">
      <div v-if="loading" class="space-y-3"><div v-for="row in 4" :key="row" class="h-14 animate-pulse rounded-xl bg-white/5" /></div>
      <template v-else>
        <p v-if="error" class="rounded-xl border border-red-400/20 bg-red-500/10 p-3 text-xs leading-5 text-red-200">{{ error }}</p>
        <div v-if="feedback" class="flex items-center gap-2 rounded-xl border border-emerald-400/15 bg-emerald-500/10 p-3 text-xs font-semibold text-emerald-200"><CheckCircleIcon class="h-5 w-5" />{{ feedback }}</div>

        <div class="rounded-xl border border-amber-400/15 bg-amber-500/5 p-4 text-xs leading-5 text-amber-100/80">
          Anyone with this link can moderate this one Webinar. Regenerating or revoking it disconnects the old link immediately, and the link stops working when the event ends.
        </div>

        <label class="block">
          <span class="text-xs font-medium text-zinc-400">Visible moderator name</span>
          <input v-model.trim="displayName" type="text" maxlength="160" class="mt-1.5 block w-full rounded-lg border-white/10 bg-black/30 px-3 py-2.5 text-sm text-zinc-200" placeholder="Moderator" />
        </label>

        <div>
          <div class="flex items-center justify-between gap-3"><span class="text-xs font-medium text-zinc-400">Moderator avatar</span><button type="button" class="text-xs font-semibold text-blue-300" @click="mediaPickerOpen = true">{{ avatarMediaFileId ? 'Change' : 'Choose from Media' }}</button></div>
          <div class="mt-2 flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-3">
            <img v-if="avatarPreview" :src="avatarPreview" alt="Moderator avatar" class="h-12 w-12 rounded-full object-cover ring-1 ring-white/10" />
            <span v-else class="flex h-12 w-12 items-center justify-center rounded-full bg-violet-500/15 text-sm font-bold text-violet-200">{{ initials }}</span>
            <div class="min-w-0"><p class="truncate text-sm font-semibold text-zinc-200">{{ displayName || 'Moderator' }}</p><p class="mt-0.5 text-xs text-zinc-500">Shown beside moderator chat messages</p></div>
          </div>
        </div>

        <div v-if="access" class="space-y-3">
          <button type="button" class="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-violet-400/20 bg-violet-500/10 px-4 py-3 text-sm font-bold text-violet-200 hover:bg-violet-500/15" :disabled="busy" @click="saveProfile"><CheckCircleIcon class="h-5 w-5" />Save moderator profile</button>
          <label class="block"><span class="text-xs font-medium text-zinc-400">Private moderator link</span><textarea :value="moderatorLink" readonly rows="4" class="mt-1.5 block w-full resize-none rounded-lg border-white/10 bg-black/30 px-3 py-2.5 text-xs leading-5 text-zinc-300" /></label>
          <button type="button" class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-500 px-4 py-3 text-sm font-bold text-white hover:bg-blue-600" @click="copyLink"><ClipboardDocumentIcon class="h-5 w-5" />Copy moderator link</button>
          <div class="grid grid-cols-2 gap-2">
            <button type="button" class="rounded-xl border border-white/10 px-3 py-2.5 text-xs font-semibold text-zinc-300 hover:bg-white/5" :disabled="busy" @click="generate">Regenerate</button>
            <button type="button" class="rounded-xl border border-red-400/15 bg-red-500/5 px-3 py-2.5 text-xs font-semibold text-red-300 hover:bg-red-500/10" :disabled="busy" @click="revoke">Revoke</button>
          </div>
          <p class="text-[11px] text-zinc-600">Expires {{ formattedExpiry }}</p>
        </div>
        <button v-else type="button" class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-500 px-4 py-3 text-sm font-bold text-white hover:bg-violet-600" :disabled="busy || !eventId" @click="generate"><LinkIcon class="h-5 w-5" />{{ busy ? 'Generating…' : 'Generate moderator link' }}</button>
      </template>
    </div>

    <WebinarMediaPickerModal :show="mediaPickerOpen" media-type="image" title="Choose moderator avatar" upload-origin="creator-webinar-moderator-avatar" z-index-class="z-[2200]" @close="mediaPickerOpen = false" @select="applyAvatar" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { CheckCircleIcon, ClipboardDocumentIcon, LinkIcon } from '@heroicons/vue/24/outline';
import WebinarMediaPickerModal from '@/Components/WebinarMediaPickerModal.vue';
import { useWebinarLiveStore } from '@/stores/webinarLiveStore';

const props = defineProps({ eventId: { type: Number, required: true }, showHeader: { type: Boolean, default: true } });
const store = useWebinarLiveStore();
const loading = ref(true);
const localBusy = ref(false);
const error = ref('');
const feedback = ref('');
const displayName = ref('Moderator');
const avatarMediaFileId = ref('');
const selectedAvatarPreview = ref('');
const mediaPickerOpen = ref(false);
const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/$/, '');
const access = computed(() => store.moderatorAccessForEvent(props.eventId));
const moderatorLink = computed(() => store.moderatorLinkForEvent(props.eventId));
const busy = computed(() => localBusy.value || store.moderatorBusy);
const avatarPreview = computed(() => selectedAvatarPreview.value || (avatarMediaFileId.value ? `${apiProxyUrl}/api/image/${encodeURIComponent(avatarMediaFileId.value)}` : ''));
const initials = computed(() => String(displayName.value || 'M').trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'M');
const formattedExpiry = computed(() => {
  const value = access.value?.expiresAt;
  if (!value) return 'when the Webinar ends';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'when the Webinar ends' : parsed.toLocaleString();
});

function hydrate(next) {
  if (!next) return;
  displayName.value = String(next.displayName || 'Moderator');
  avatarMediaFileId.value = String(next.avatarMediaFileId || '');
  selectedAvatarPreview.value = '';
}
function success(message) { feedback.value = message; window.setTimeout(() => { feedback.value = ''; }, 3200); }
async function generate() {
  localBusy.value = true; error.value = '';
  try { const payload = await store.generateModeratorAccess(props.eventId, { displayName: displayName.value || 'Moderator', avatarMediaFileId: avatarMediaFileId.value || null }); hydrate(payload.access); success('Moderator link is ready'); }
  catch (requestError) { error.value = requestError?.message || 'Unable to generate the moderator link.'; }
  finally { localBusy.value = false; }
}
async function saveProfile() {
  localBusy.value = true; error.value = '';
  try {
    const payload = await store.updateModeratorAccess(props.eventId, { displayName: displayName.value || 'Moderator', avatarMediaFileId: avatarMediaFileId.value || null });
    hydrate(payload.access); success('Moderator profile updated');
  } catch (requestError) { error.value = requestError?.message || 'Unable to update the moderator profile.'; }
  finally { localBusy.value = false; }
}
async function revoke() {
  if (!window.confirm('Revoke this moderator link? Anyone using it will be disconnected.')) return;
  localBusy.value = true; error.value = '';
  try { await store.revokeModeratorAccess(props.eventId); success('Moderator access revoked'); }
  catch (requestError) { error.value = requestError?.message || 'Unable to revoke moderator access.'; }
  finally { localBusy.value = false; }
}
async function copyLink() {
  try { await navigator.clipboard.writeText(moderatorLink.value); success('Moderator link copied'); }
  catch (_error) { error.value = 'Your browser blocked clipboard access. Select and copy the link manually.'; }
}
function applyAvatar(media) { avatarMediaFileId.value = String(media?.fileId || ''); selectedAvatarPreview.value = String(media?.previewUrl || ''); mediaPickerOpen.value = false; }
onMounted(async () => {
  try { const payload = await store.fetchModeratorAccess(props.eventId); hydrate(payload.access); }
  catch (requestError) { error.value = requestError?.message || 'Unable to load moderator access.'; }
  finally { loading.value = false; }
});
</script>
